// console-interceptor.service.ts
import { Injectable } from '@angular/core';
import { ExternalConfService } from '@core/services/external-conf.service';
import { environment } from '@env/environment';

// debug-level.enum.ts
export enum DebugLevel {
   NONE = 0, // No muestra ningún log
   ERROR = 1, // Muestra solo errores
   WARN = 2, // Muestra warnings y errores
   INFO = 3, // Muestra info, warn y error
   LOG = 4, // Muestra log, info, warn, error
   DEBUG = 5  // Muestra absolutamente todo (log, info, warn, error, debug)
}

const ACTIVAR_PREFIX = true; // Activa o desactiva el prefijo con metadatos en cada mensaje log


// info     Posibles mejoras:
// info      * Poder Cambiar dinámicamente el nivel de logs: si debugLevel varía en tiempo de ejecución, se podría exponer un método que reasigne la variable this.currentLevel.
// info      * Enviar logs a un servidor (por ejemplo a Sentry o un endpoint propio) cuando el nivel sea ERROR o superior. Así obtienes un registro remoto de errores.
// info      * Incluir un prefijo con metadatos o timestamp en cada mensaje log, si el proyecto lo requiere.


@Injectable({
   providedIn: 'root'
})
export class ConsoleInterceptor {

   // Almacenamos referencias a los métodos originales
   private originalLog = console.log;
   private originalWarn = console.warn;
   private originalError = console.error;
   private originalInfo = console.info;
   private originalDebug = console.debug;

   // Nivel de log actual
   private currentLevel: DebugLevel = DebugLevel.NONE;

   constructor(private externalConfService: ExternalConfService) { }

   /**
    * Inicializamos el interceptor de consola. solo en producción
    */
   init(): void {
      if (environment.production) {
         this.currentLevel = this.mapDebugLevel(this.externalConfService.DEBUG_LEVEL);
         this.initInterceptor();
      }
   }

   /**
    * Transforma un número a un DebugLevel válido
    */
   private mapDebugLevel(level: number): DebugLevel {
      if (level < 0) return DebugLevel.NONE;
      if (level > 5) return DebugLevel.DEBUG;
      return level as DebugLevel;
   }

   /**
    * Actualiza el nivel de debug en tiempo de ejecución.
    */
   updateDebugLevel(level: DebugLevel): void {
      this.currentLevel = level;
   }

   /**
    * Inicializa el "monkey patch" de los métodos de console
    */
   private initInterceptor(): void {
      console.log = (...args: any[]): void => {
         if (this.currentLevel >= DebugLevel.LOG) {
            this.originalLog.apply(console,
               ACTIVAR_PREFIX ? this.addPrefix('LOG', args) : args
            );
         }
      };

      console.warn = (...args: any[]): void => {
         if (this.currentLevel >= DebugLevel.WARN) {
            this.originalWarn.apply(console,
               ACTIVAR_PREFIX ? this.addPrefix('WARN', args) : args
            );
         }
      };

      console.error = (...args: any[]): void => {
         if (this.currentLevel >= DebugLevel.ERROR) {
            this.originalError.apply(console,
               ACTIVAR_PREFIX ? this.addPrefix('ERROR', args) : args
            );
         }
      };

      console.info = (...args: any[]): void => {
         if (this.currentLevel >= DebugLevel.INFO) {
            this.originalInfo.apply(console,
               ACTIVAR_PREFIX ? this.addPrefix('INFO', args) : args
            );
         }
      };

      console.debug = (...args: any[]): void => {
         if (this.currentLevel >= DebugLevel.DEBUG) {
            this.originalDebug.apply(console,
               ACTIVAR_PREFIX ? this.addPrefix('DEBUG', args) : args
            );
         }
      };
   }


   /**
    * Método para añadir un prefijo con timestamp y metadata
    */
   private addPrefix(levelStr: string, args: any[]): any[] {
      // queremos la fechha y la hora en formato YY-MM-DD HH:mm:ss (sin milisegundos)
      const timestamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
      // Podemos añadir más metadatos aquí según vayamos necesitando
      // Por ejemplo, un ID de usuario o la URL actual
      const prefix = `[${timestamp}][${levelStr}]`;

      // Retorna un array con el prefijo + args originales
      return [prefix, ...args];
   }
}
